import { ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { DocsSearch } from "@/components/docs/DocsSearch";
import { docHref, getDocPage, getDocSections } from "@/content/docs/index";
import { cn } from "@/lib/cn";
import { docsSlugFromPath } from "@/lib/docsPath";

/**
 * Section navigation.
 *
 * On small screens it collapses into a `<details>` disclosure — native, needs
 * no JavaScript, and stays expandable in the prerendered HTML a crawler sees.
 */
export function DocsSidebar() {
  const location = useLocation();
  const current = docsSlugFromPath(location.pathname);

  return (
    <aside className="mb-10 lg:mb-0">
      <div className="lg:sticky lg:top-24 lg:pb-10">
        <DocsSearch />

        <details className="group mt-4 lg:hidden" name="docs-nav">
          <summary className="flex cursor-pointer list-none items-center justify-between rounded-md border border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground">
            Browse docs
            <ChevronDown
              className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180"
              aria-hidden
            />
          </summary>
          <SectionNav current={current} groupName="docs-mobile-section" className="mt-4" />
        </details>

        <SectionNav
          current={current}
          groupName="docs-desktop-section"
          className="mt-6 hidden lg:block"
        />
      </div>
    </aside>
  );
}

function SectionNav({
  current,
  groupName,
  className,
}: {
  current: string;
  groupName: string;
  className?: string;
}) {
  return (
    <nav className={cn("space-y-2 text-sm", className)} aria-label="Documentation">
      {getDocSections().map((section) => {
        const activeSection = section.pages.includes(current);
        return (
          <details key={section.id} name={groupName} open={activeSection} className="group/section">
            <summary
              className={cn(
                "flex cursor-pointer list-none items-center justify-between rounded-md px-2 py-2 text-xs font-semibold tracking-wide transition-colors lg:py-1.5",
                activeSection
                  ? "text-foreground"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
              )}
            >
              {section.label}
              <ChevronDown
                className="h-3.5 w-3.5 transition-transform group-open/section:rotate-180"
                aria-hidden
              />
            </summary>
            <ul className="mt-1 space-y-0.5">
              {section.pages.map((slug) => {
                const page = getDocPage(slug);
                if (!page) return null;
                const active = current === slug;
                return (
                  <li key={slug}>
                    <Link
                      to={docHref(slug)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "block rounded-md px-2 py-2 leading-snug transition-colors lg:py-1.5",
                        active
                          ? "bg-muted font-medium text-foreground"
                          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                      )}
                    >
                      {slug === "index" ? "Overview" : (page.navLabel ?? page.title)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </details>
        );
      })}
    </nav>
  );
}
